<template>
    <div class="ktp">
        <div class="ktp-labels">
            <div class="ktp-label">{{ $tc('common.hours', 2) }}</div>
            <div class="ktp-label">{{ $tc('common.minutes', 2) }}</div>
            <div class="ktp-label">{{ $tc('common.seconds', 2) }}</div>
        </div>
        <div class="ktp-wheels">
            <div class="ktp-wheel" ref="hoursWheel" @scroll="onScroll('hours', 'hoursWheel')"
                @pointerdown="onPointerDown($event, 'hours', 'hoursWheel')">
                <div class="ktp-spacer"></div>
                <div v-for="n in 24" :key="n" class="ktp-item" :class="{ 'ktp-item--active': n - 1 === hours }"
                    @click="select('hours', 'hoursWheel', n - 1)">{{ pad(n - 1) }}</div>
                <div class="ktp-spacer"></div>
            </div>
            <div class="ktp-wheel" ref="minutesWheel" @scroll="onScroll('minutes', 'minutesWheel')"
                @pointerdown="onPointerDown($event, 'minutes', 'minutesWheel')">
                <div class="ktp-spacer"></div>
                <div v-for="n in 60" :key="n" class="ktp-item" :class="{ 'ktp-item--active': n - 1 === minutes }"
                    @click="select('minutes', 'minutesWheel', n - 1)">{{ pad(n - 1) }}</div>
                <div class="ktp-spacer"></div>
            </div>
            <div class="ktp-wheel" ref="secondsWheel" @scroll="onScroll('seconds', 'secondsWheel')"
                @pointerdown="onPointerDown($event, 'seconds', 'secondsWheel')">
                <div class="ktp-spacer"></div>
                <div v-for="n in 60" :key="n" class="ktp-item" :class="{ 'ktp-item--active': n - 1 === seconds }"
                    @click="select('seconds', 'secondsWheel', n - 1)">{{ pad(n - 1) }}</div>
                <div class="ktp-spacer"></div>
            </div>
            <div class="ktp-band"></div>
        </div>
    </div>
</template>

<script>
export default {
    name: "KimsaTimePicker",
    props: {
        value: {
            type: String,
            default: '00:00:00'
        }
    },
    data() {
        return {
            hours: 0,
            minutes: 0,
            seconds: 0,
            itemHeight: 40,
            timers: {},
            drag: null,
            suppressClick: false,
        }
    },
    computed: {
        time(vm = this) {
            return `${vm.pad(vm.hours)}:${vm.pad(vm.minutes)}:${vm.pad(vm.seconds)}`
        },
    },
    mounted() {
        this.setDefault()
    },
    beforeDestroy() {
        window.removeEventListener('pointermove', this.onPointerMove)
        window.removeEventListener('pointerup', this.onPointerUp)
    },
    methods: {
        pad(n) {
            return String(n).padStart(2, '0')
        },
        onScroll(unit, ref) {
            const vm = this
            const el = vm.$refs[ref]
            if (!el) return
            const index = Math.round(el.scrollTop / vm.itemHeight)
            vm[unit] = index
            clearTimeout(vm.timers[unit])
            vm.timers[unit] = setTimeout(() => vm.$emit('change', vm.time), 120)
        },
        select(unit, ref, index) {
            const vm = this
            if (vm.suppressClick) {
                vm.suppressClick = false
                return
            }
            const el = vm.$refs[ref]
            if (!el) return
            el.scrollTo({ top: index * vm.itemHeight, behavior: 'smooth' })
        },
        onPointerDown(e, unit, ref) {
            const vm = this
            if (e.pointerType && e.pointerType !== 'mouse') return
            const el = vm.$refs[ref]
            if (!el) return
            vm.suppressClick = false
            vm.drag = { ref, startY: e.clientY, startScroll: el.scrollTop, moved: false }
            el.style.scrollSnapType = 'none'
            window.addEventListener('pointermove', vm.onPointerMove)
            window.addEventListener('pointerup', vm.onPointerUp)
        },
        onPointerMove(e) {
            const vm = this
            if (!vm.drag) return
            const el = vm.$refs[vm.drag.ref]
            if (!el) return
            const delta = e.clientY - vm.drag.startY
            if (Math.abs(delta) > 3) vm.drag.moved = true
            el.scrollTop = vm.drag.startScroll - delta
        },
        onPointerUp() {
            const vm = this
            window.removeEventListener('pointermove', vm.onPointerMove)
            window.removeEventListener('pointerup', vm.onPointerUp)
            if (!vm.drag) return
            const el = vm.$refs[vm.drag.ref]
            vm.suppressClick = vm.drag.moved
            vm.drag = null
            if (el) {
                el.style.scrollSnapType = ''
                const index = Math.round(el.scrollTop / vm.itemHeight)
                el.scrollTo({ top: index * vm.itemHeight, behavior: 'smooth' })
            }
        },
        scrollTo(ref, index) {
            const vm = this
            const el = vm.$refs[ref]
            if (el) el.scrollTop = index * vm.itemHeight
        },
        setDefault() {
            const vm = this
            const parts = (vm.value || '00:00:00').split(':')
            vm.hours = Number(parts[0]) || 0
            vm.minutes = Number(parts[1]) || 0
            vm.seconds = Number(parts[2]) || 0
            vm.$nextTick(() => {
                vm.scrollTo('hoursWheel', vm.hours)
                vm.scrollTo('minutesWheel', vm.minutes)
                vm.scrollTo('secondsWheel', vm.seconds)
            })
        },
    },
    watch: {
        value() {
            this.setDefault()
        },
    }
}
</script>

<style scoped>
.ktp {
    display: flex;
    flex-direction: column;
}

.ktp-labels {
    display: flex;
    gap: 10px;
}

.ktp-label {
    flex: 1;
    text-align: center;
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    margin-bottom: 6px;
}

.ktp-wheels {
    display: flex;
    gap: 10px;
    position: relative;
}

.ktp-wheel {
    flex: 1;
    height: 120px;
    overflow-y: auto;
    scroll-snap-type: y mandatory;
    -webkit-mask-image: linear-gradient(to bottom, transparent 0%, #000 33%, #000 67%, transparent 100%);
    mask-image: linear-gradient(to bottom, transparent 0%, #000 33%, #000 67%, transparent 100%);
    scrollbar-width: none;
    cursor: grab;
    user-select: none;
    -webkit-user-select: none;
}

.ktp-wheel:active {
    cursor: grabbing;
}

.ktp-wheel::-webkit-scrollbar {
    display: none;
}

.ktp-spacer {
    height: 40px;
    flex-shrink: 0;
}

.ktp-item {
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: var(--v-txtMuted-base);
    scroll-snap-align: center;
    cursor: pointer;
    transition: color .15s ease, transform .15s ease;
}

.ktp-item--active {
    color: var(--v-txtPrimary-base);
    font-weight: 700;
    transform: scale(1.08);
}

.ktp-band {
    position: absolute;
    left: 0;
    right: 0;
    top: 40px;
    height: 40px;
    border-top: 1px solid var(--v-outline-base);
    border-bottom: 1px solid var(--v-outline-base);
    pointer-events: none;
}
</style>
